import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Landmark, CreditCard, Search, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, ArrowUpDown, TrendingUp, TrendingDown, Minus,
  Pencil, Save, X, Tag, Briefcase, User, Calendar, Check, Download,
  Link2, Unlink, ChevronDown, ChevronUp as ChevronUpIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DEFAULT_CATEGORIES = ["Travel", "Food & Dining", "Shopping", "Transportation", "Services", "Subscriptions", "Utilities", "Entertainment", "Health", "Education", "Fees & Charges", "Home", "Personal Care", "Insurance", "Fuel", "Other"];
const INCOME_CATEGORIES = ["Salary", "Freelance", "Business Income", "Investment Returns", "Interest", "Rental Income", "Refund", "Cashback", "Gift", "Other Income"];
const fmtINR = (v) => v?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || '0';

function TagBadge({ tag, tagSource }) {
  const styles = {
    business: 'bg-[#D36135]/10 text-[#D36135] border-[#D36135]/20',
    personal: 'bg-[#4A7A5D]/10 text-[#4A7A5D] border-[#4A7A5D]/20',
    income: 'bg-blue-100 text-blue-700 border-blue-200',
    untagged: 'bg-[#E8E6DE] text-[#5C5C55] border-[#E2E0D8]',
  };
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wide ${styles[tag] || styles.untagged}`}>
      {tag === 'business' && <Briefcase size={10} />}
      {tag === 'personal' && <User size={10} />}
      {tag === 'income' && <TrendingUp size={10} />}
      {tag === 'untagged' && <Tag size={10} />}
      {tag}
    </span>
  );
}

function SortHeader({ label, field, currentSort, onSort }) {
  const isActive = currentSort.field === field;
  const isAsc = isActive && currentSort.order === 'asc';
  return (
    <button onClick={() => onSort(field)} data-testid={`sort-${field}`}
      className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-medium hover:text-[#1C1C19] transition-colors">
      {label}
      {isActive ? (isAsc ? <ArrowUp size={10} className="text-[#284A33]" /> : <ArrowDown size={10} className="text-[#284A33]" />) : <ArrowUpDown size={10} className="opacity-30" />}
    </button>
  );
}

export default function AccountDetailPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [linksMap, setLinksMap] = useState({});
  const [expandedLinks, setExpandedLinks] = useState(new Set());
  const [summary, setSummary] = useState({ inflow_total: 0, outflow_total: 0, net: 0, inflow_count: 0, outflow_count: 0 });
  const [availableFys, setAvailableFys] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [fy, setFy] = useState('all');
  const [flow, setFlow] = useState('all');
  const [tag, setTag] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ field: 'date', order: 'desc' });

  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({ tag: '', category: '' });
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [catDialog, setCatDialog] = useState({ open: false, customName: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, limit: 50, sort_by: sort.field, sort_order: sort.order,
      };
      if (fy !== 'all') params.fy = fy;
      if (flow !== 'all') params.flow = flow;
      if (tag !== 'all') params.tag = tag;
      if (search) params.search = search;

      const { data } = await axios.get(`${API}/cards/${accountId}/transactions`, { params, withCredentials: true });
      setAccount(data.account);
      setTransactions(data.transactions);
      setLinksMap(data.links || {});
      setSummary(data.summary);
      setTotal(data.total);
      setPages(data.pages);
      setAvailableFys(data.available_fys || []);
    } catch (err) {
      console.error('Failed to load account transactions:', err);
      toast.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  }, [accountId, page, sort, fy, flow, tag, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    axios.get(`${API}/categories`, { withCredentials: true })
      .then(({ data }) => setCategories(data.categories || DEFAULT_CATEGORIES))
      .catch(() => {});
  }, []);

  const handleSort = (field) => {
    setSort(prev => ({ field, order: prev.field === field ? (prev.order === 'desc' ? 'asc' : 'desc') : 'asc' }));
    setPage(1);
  };

  const startEdit = (tx) => {
    setEditingRow(tx.id);
    setEditData({ tag: tx.tag || 'untagged', category: tx.category || 'Other' });
  };
  const cancelEdit = () => { setEditingRow(null); };

  const saveEdit = async (txId) => {
    try {
      const tx = transactions.find(t => t.id === txId);
      const promises = [];
      if (editData.tag !== tx.tag) {
        promises.push(axios.patch(`${API}/transactions/${txId}/tag`, { tag: editData.tag }, { withCredentials: true }));
      }
      if (editData.category !== tx.category) {
        promises.push(axios.patch(`${API}/transactions/${txId}/category`, { category: editData.category }, { withCredentials: true }));
      }
      await Promise.all(promises);
      toast.success('Saved');
      setEditingRow(null);
      fetchData();
    } catch { toast.error('Save failed'); }
  };

  const handleCatChange = (v) => {
    if (v === '__add__') {
      setCatDialog({ open: true, customName: '' });
      return;
    }
    setEditData(d => ({ ...d, category: v }));
  };

  const saveCustomCategory = async () => {
    const name = catDialog.customName.trim();
    if (!name) { toast.error('Enter a name'); return; }
    try {
      await axios.post(`${API}/categories`, { name }, { withCredentials: true });
      const { data } = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(data.categories || DEFAULT_CATEGORIES);
      setEditData(d => ({ ...d, category: name }));
      setCatDialog({ open: false, customName: '' });
      toast.success(`"${name}" added`);
    } catch { toast.error('Failed'); }
  };

  const toggleLinkExpand = (linkId) => {
    setExpandedLinks(prev => { const n = new Set(prev); n.has(linkId) ? n.delete(linkId) : n.add(linkId); return n; });
  };

  const unlinkGroup = async (linkId) => {
    try {
      await axios.delete(`${API}/transactions/link/${linkId}`, { withCredentials: true });
      toast.success('Transactions unlinked');
      fetchData();
    } catch { toast.error('Unlink failed'); }
  };

  // Group linked transactions
  const groupedTransactions = (() => {
    const result = [];
    const seen = new Set();
    for (const tx of transactions) {
      if (tx.link_id && linksMap[tx.link_id]) {
        if (seen.has(tx.link_id)) continue;
        seen.add(tx.link_id);
        const linkInfo = linksMap[tx.link_id];
        const children = transactions.filter(t => t.link_id === tx.link_id);
        const creditSum = children.filter(c => c.is_credit).reduce((s, c) => s + c.amount, 0);
        result.push({
          _isGroup: true,
          link_id: tx.link_id,
          note: linkInfo.note,
          total_amount: linkInfo.total_amount,
          earliest_date: linkInfo.earliest_date,
          children,
          tag: children[0]?.tag || 'untagged',
          category: children[0]?.category,
          is_credit: Math.abs(creditSum - linkInfo.total_amount) < 1,
        });
      } else {
        result.push(tx);
      }
    }
    return result;
  })();

  const isBankAccount = account?.card_type === 'bank_account';
  const isClosed = account?.status === 'closed';

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="account-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/accounts')} data-testid="back-to-accounts"
          className="p-2 rounded-md hover:bg-[#E8E6DE] transition-colors text-[#5C5C55] hover:text-[#1C1C19]">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isBankAccount ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'} ${isClosed ? 'opacity-50' : ''}`}>
              {isBankAccount ? <Landmark size={20} /> : <CreditCard size={20} />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-xl sm:text-2xl tracking-tight text-[#1C1C19]">
                  {account?.card_name || 'Loading...'}
                </h2>
                {isClosed && (
                  <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded font-mono uppercase">Closed {account?.closed_date || ''}</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[#5C5C55]">
                <span className="font-mono">{account?.account_number || ''}</span>
                {account?.issuer && <span>• {account.issuer}</span>}
                {account?.account_type && <span>• {account.account_type}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4" data-testid="account-summary">
        <div className="bg-white border border-[#E2E0D8] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-[#4A7A5D]" />
            <span className="text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium">Total Inflow</span>
          </div>
          <div className="text-lg font-mono font-semibold text-[#4A7A5D]" data-testid="summary-inflow">
            +₹{fmtINR(summary.inflow_total)}
          </div>
          <div className="text-[10px] text-[#5C5C55] font-mono mt-0.5">{summary.inflow_count} credits</div>
        </div>
        <div className="bg-white border border-[#E2E0D8] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown size={14} className="text-[#D36135]" />
            <span className="text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium">Total Outflow</span>
          </div>
          <div className="text-lg font-mono font-semibold text-[#D36135]" data-testid="summary-outflow">
            -₹{fmtINR(summary.outflow_total)}
          </div>
          <div className="text-[10px] text-[#5C5C55] font-mono mt-0.5">{summary.outflow_count} debits</div>
        </div>
        <div className="bg-white border border-[#E2E0D8] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Minus size={14} className="text-[#1C1C19]" />
            <span className="text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium">Net Flow</span>
          </div>
          <div className={`text-lg font-mono font-semibold ${summary.net >= 0 ? 'text-[#4A7A5D]' : 'text-[#D36135]'}`} data-testid="summary-net">
            {summary.net >= 0 ? '+' : ''}₹{fmtINR(Math.abs(summary.net))}
          </div>
          <div className="text-[10px] text-[#5C5C55] font-mono mt-0.5">{total} total transactions</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="account-filters">
        <div className="relative flex-1 min-w-[160px] max-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C55]" />
          <Input data-testid="account-search" placeholder="Search transactions..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8 border-[#E2E0D8] h-8 text-xs" />
        </div>
        <Select value={fy} onValueChange={(v) => { setFy(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-fy" className="w-[130px] h-8 border-[#E2E0D8] text-xs">
            <Calendar size={12} className="mr-1 text-[#5C5C55]" /><SelectValue placeholder="All FYs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All FYs</SelectItem>
            {availableFys.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tag} onValueChange={(v) => { setTag(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-tag" className="w-[120px] h-8 border-[#E2E0D8] text-xs">
            <Tag size={12} className="mr-1 text-[#5C5C55]" /><SelectValue placeholder="All Tags" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            <SelectItem value="untagged">Untagged</SelectItem>
            <SelectItem value="tagged">Tagged</SelectItem>
            <SelectItem value="personal">Personal</SelectItem>
            <SelectItem value="business">Business</SelectItem>
            <SelectItem value="income">Income</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 ml-auto" data-testid="flow-tabs">
          {['all', 'inflow', 'outflow'].map(f => (
            <button key={f} onClick={() => { setFlow(f); setPage(1); }} data-testid={`flow-${f}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${flow === f ? 'bg-[#284A33] text-white' : 'text-[#5C5C55] hover:bg-[#E8E6DE]'}`}>
              {f === 'all' ? 'All' : f === 'inflow' ? 'Inflow' : 'Outflow'}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-[#5C5C55] font-mono">{total} results</span>
      </div>

      {/* Transaction Table */}
      <div className="bg-white border border-[#E2E0D8] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#5C5C55]">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#5C5C55]">No transactions found for this account</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="account-transactions-table">
              <thead>
                <tr className="border-b border-[#E2E0D8] bg-[#F7F6F2]/50">
                  <th className="text-left p-2 text-[#5C5C55]"><SortHeader label="Date" field="date" currentSort={sort} onSort={handleSort} /></th>
                  <th className="text-left p-2 text-[#5C5C55]"><SortHeader label="Merchant / Description" field="merchant" currentSort={sort} onSort={handleSort} /></th>
                  <th className="text-left p-2 text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium">Tag / Category</th>
                  <th className="text-right p-2 text-[#5C5C55]"><SortHeader label="Amount" field="amount" currentSort={sort} onSort={handleSort} /></th>
                  <th className="w-10 p-2 text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium text-center">Edit</th>
                </tr>
              </thead>
              <tbody>
                {groupedTransactions.map((item) => {
                  if (item._isGroup) {
                    const isExpanded = expandedLinks.has(item.link_id);
                    return (
                      <React.Fragment key={`link-${item.link_id}`}>
                        <tr data-testid={`linked-group-${item.link_id}`}
                          className="border-b border-blue-200/60 bg-blue-50/30 hover:bg-blue-50/60 cursor-pointer"
                          onClick={() => toggleLinkExpand(item.link_id)}>
                          <td className="p-2">
                            <div className="flex items-center gap-1.5">
                              {isExpanded ? <ChevronUpIcon size={14} className="text-blue-600" /> : <ChevronDown size={14} className="text-blue-600" />}
                              <span className="text-sm font-mono text-[#1C1C19]">{item.earliest_date}</span>
                            </div>
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-1.5">
                              <Link2 size={12} className="text-blue-600 shrink-0" />
                              <span className="text-sm font-medium text-[#1C1C19]">Split Entry ({item.children.length})</span>
                            </div>
                            {item.note && <div className="text-[10px] text-blue-600 mt-0.5">{item.note}</div>}
                          </td>
                          <td className="p-2">
                            <TagBadge tag={item.tag} tagSource={item.children?.[0]?.tag_source} />
                            <span className="text-[10px] text-[#5C5C55] block">{item.category}</span>
                          </td>
                          <td className="p-2 text-right">
                            <span className={`font-mono text-sm font-semibold ${item.is_credit ? 'text-[#4A7A5D]' : 'text-[#1C1C19]'}`}>
                              {item.is_credit ? '+' : ''}₹{fmtINR(item.total_amount)}
                            </span>
                            <div className="text-[10px] text-blue-600 font-mono">combined</div>
                          </td>
                          <td className="p-2">
                            <button onClick={(e) => { e.stopPropagation(); unlinkGroup(item.link_id); }} title="Unlink"
                              className="p-1 rounded text-[#5C5C55] hover:text-red-600 hover:bg-red-50"><Unlink size={12} /></button>
                          </td>
                        </tr>
                        {isExpanded && item.children.map((tx) => (
                          <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}
                            className="border-b border-blue-100/60 bg-blue-50/15">
                            <td className="p-2 pl-6">
                              <div className="flex items-center gap-1.5">
                                <div className="w-px h-4 bg-blue-300" />
                                <span className="text-sm font-mono text-[#1C1C19]/70">{tx.date}</span>
                              </div>
                            </td>
                            <td className="p-2">
                              <div title={tx.merchant} className="text-sm text-[#1C1C19]/70 truncate max-w-[300px]">{tx.merchant}</div>
                            </td>
                            <td className="p-2">
                              <TagBadge tag={tx.tag} tagSource={tx.tag_source} />
                              <span className="text-[10px] text-[#5C5C55] block">{tx.category}</span>
                            </td>
                            <td className="p-2 text-right">
                              <span className={`font-mono text-sm ${tx.is_credit ? 'text-[#4A7A5D]/70' : 'text-[#1C1C19]/70'}`}>
                                {tx.is_credit ? '+' : ''}₹{fmtINR(tx.amount)}
                              </span>
                            </td>
                            <td className="p-2" />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }

                  // Regular transaction row
                  const tx = item;
                  const isEditing = editingRow === tx.id;

                  return (
                    <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}
                      className={`border-b border-[#E2E0D8]/60 transition-colors ${isEditing ? 'bg-[#284A33]/[0.02]' : 'hover:bg-[#F7F6F2]/50'}`}>
                      <td className="p-2">
                        <div className="text-sm font-mono whitespace-nowrap text-[#1C1C19]">{tx.date}</div>
                        <div className="text-[10px] font-mono text-[#5C5C55]/60">{tx.fy_india}</div>
                      </td>
                      <td className="p-2">
                        <div title={tx.merchant} className="text-sm font-medium truncate max-w-[300px] text-[#1C1C19]">{tx.merchant}</div>
                        {tx.location && <div className="text-[10px] text-[#5C5C55] truncate max-w-[200px]">{tx.location}</div>}
                      </td>
                      <td className="p-2">
                        {isEditing ? (
                          <div className="flex flex-col gap-1.5">
                            <Select value={editData.tag} onValueChange={(v) => setEditData(d => ({ ...d, tag: v }))}>
                              <SelectTrigger className="h-7 w-[120px] text-xs border-[#284A33]/30"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="untagged">Untagged</SelectItem>
                                {tx.is_credit ? (
                                  <SelectItem value="income">Income</SelectItem>
                                ) : (
                                  <><SelectItem value="personal">Personal</SelectItem><SelectItem value="business">Business</SelectItem></>
                                )}
                              </SelectContent>
                            </Select>
                            <Select value={editData.category} onValueChange={handleCatChange}>
                              <SelectTrigger className="h-7 w-[120px] text-xs border-[#E2E0D8]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(tx.is_credit ? INCOME_CATEGORIES : categories).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                <SelectItem value="__add__" className="text-[#284A33] font-medium">+ Add Category</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <TagBadge tag={tx.tag} tagSource={tx.tag_source} />
                            {tx.sub_category && <span className="text-[10px] text-[#5C5C55] font-mono">{tx.sub_category}</span>}
                            <span className="text-[10px] text-[#5C5C55]">{tx.category}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <span className={`font-mono text-sm ${tx.is_credit ? 'text-[#4A7A5D]' : 'text-[#1C1C19]'}`}>
                          {tx.is_credit ? '+' : ''}₹{fmtINR(tx.amount)}
                        </span>
                        {tx.foreign_amount && (
                          <div className="text-[10px] font-mono text-[#5C5C55] flex items-center justify-end gap-1">
                            <span className="bg-[#E8E6DE] px-1 py-0 rounded text-[9px]">{tx.country || tx.foreign_currency}</span>
                            {tx.foreign_currency} {tx.foreign_amount}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-0.5 justify-center">
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEdit(tx.id)} data-testid={`save-${tx.id}`} title="Save"
                                className="p-1 rounded text-[#284A33] hover:bg-[#284A33]/10"><Save size={13} /></button>
                              <button onClick={cancelEdit} data-testid={`cancel-${tx.id}`} title="Cancel"
                                className="p-1 rounded text-[#5C5C55] hover:bg-[#E8E6DE]"><X size={13} /></button>
                            </>
                          ) : (
                            <button onClick={() => startEdit(tx)} data-testid={`edit-${tx.id}`} title="Edit"
                              className="p-1 rounded text-[#5C5C55] hover:text-[#284A33] hover:bg-[#284A33]/10"><Pencil size={12} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E0D8] bg-[#F7F6F2]/30">
            <span className="text-xs text-[#5C5C55]">Page {page} of {pages}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-7 w-7 p-0"><ChevronLeft size={15} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="h-7 w-7 p-0"><ChevronRight size={15} /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Custom Category Dialog */}
      <Dialog open={catDialog.open} onOpenChange={(o) => !o && setCatDialog({ ...catDialog, open: false })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Custom Category</DialogTitle>
            <DialogDescription>Added to your categories permanently.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Category Name</Label>
            <Input data-testid="custom-category-input" value={catDialog.customName}
              onChange={(e) => setCatDialog({ ...catDialog, customName: e.target.value })}
              placeholder="e.g. Gym & Fitness" className="border-[#E2E0D8]"
              onKeyDown={(e) => e.key === 'Enter' && saveCustomCategory()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatDialog({ ...catDialog, open: false })}>Cancel</Button>
            <Button onClick={saveCustomCategory} className="bg-[#284A33] text-white hover:bg-[#1C3624]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
